import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ChevronLeft, Phone, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customerAPI, customerPaymentAPI, envTaxAPI, fitnessAPI, insuranceAPI, licenceAPI, pucAPI, roadTaxAPI, vehicleAPI } from '../api/index';
import { Button } from '../components/Button';
import { ExpiryBadge } from '../components/ExpiryBadge';
import { AddCustomerModal } from '../components/modals/AddCustomerModal';
import AddEnvTaxModal from '../components/modals/AddEnvTaxModal';
import AddFitnessModal from '../components/modals/AddFitnessModal';
import AddInsuranceModal from '../components/modals/AddInsuranceModal';
import AddLicenceModal from '../components/modals/AddLicenceModal';
import AddPUCModal from '../components/modals/AddPUCModal';
import AddRoadTaxModal from '../components/modals/AddRoadTaxModal';
import AddVehicleModal from '../components/modals/AddVehicleModal';
import { PaymentForm } from '../components/PaymentForm';
import { daysRemaining, formatDate, formatDateTime, getInitials } from '../lib/utils';
import type { Insurance, Licence, PUC } from '../types/index';

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'vehicles' | 'licences' | 'insurances' | 'pucs' | 'fitness' | 'road_tax' | 'env_tax' | 'payments'>('vehicles')
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false)
  const [showAddInsuranceModal, setShowAddInsuranceModal] = useState(false)
  const [showLicenceModal, setShowLicenceModal] = useState(false)
  const [showAddPUCModal, setShowAddPUCModal] = useState(false)
  const [showAddFitnessModal, setShowAddFitnessModal] = useState(false)
  const [showAddRoadTaxModal, setShowAddRoadTaxModal] = useState(false)
  const [showAddEnvTaxModal, setShowAddEnvTaxModal] = useState(false)
  const [selectedLicence, setSelectedLicence] = useState<Licence | undefined>()
  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | undefined>()
  const [selectedPUC, setSelectedPUC] = useState<PUC | undefined>()
  const [selectedFitness, setSelectedFitness] = useState<any | undefined>()
  const [selectedRoadTax, setSelectedRoadTax] = useState<any | undefined>()
  const [selectedEnvTax, setSelectedEnvTax] = useState<any | undefined>()

  const { data: customer, isLoading: customerLoading, error: customerError } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customerAPI.getSummary(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: vehicles, isLoading: vehiclesLoading } = useQuery({
    queryKey: ['vehicles', id],
    queryFn: () => vehicleAPI.listForCustomer(id!).then(r => r.data?.vehicles || []),
    enabled: !!id && activeTab === 'vehicles',
    placeholderData: [],
  })

  const { data: licences, isLoading: licencesLoading } = useQuery({
    queryKey: ['licences', id],
    queryFn: () => licenceAPI.listForCustomer(id!).then(r => r.data?.licences || []),
    enabled: !!id && activeTab === 'licences',
    placeholderData: [],
  })

  const { data: insurances, isLoading: insurancesLoading } = useQuery({
    queryKey: ['insurances', id],
    queryFn: () => insuranceAPI.listForCustomer(id!).then(r => r.data?.insurances || []),
    enabled: !!id && activeTab === 'insurances',
    placeholderData: [],
  })

  const { data: pucs, isLoading: pucsLoading } = useQuery({
    queryKey: ['pucs', id],
    queryFn: () => pucAPI.listForCustomer(id!).then(r => r.data?.pucs || []),
    enabled: !!id && activeTab === 'pucs',
    placeholderData: [],
  })

  const { data: fitness, isLoading: fitnessLoading } = useQuery({
    queryKey: ['fitness', id],
    queryFn: () => fitnessAPI.listForCustomer(id!).then(r => r.data?.fitness || []),
    enabled: !!id && activeTab === 'fitness',
    placeholderData: [],
  })

  const { data: roadTaxes, isLoading: roadTaxLoading } = useQuery({
    queryKey: ['road_taxes', id],
    queryFn: () => roadTaxAPI.listForCustomer(id!).then(r => r.data?.road_taxes || []),
    enabled: !!id && activeTab === 'road_tax',
    placeholderData: [],
  })

  const { data: envTaxes, isLoading: envTaxLoading } = useQuery({
    queryKey: ['env_taxes', id],
    queryFn: () => envTaxAPI.listForCustomer(id!).then(r => r.data?.env_taxes || []),
    enabled: !!id && activeTab === 'env_tax',
    placeholderData: [],
  })

  const { data: paymentSummary, isLoading: paymentSummaryLoading } = useQuery({
    queryKey: ['payment_summary', id],
    queryFn: () => customerPaymentAPI.getSummary(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ['payments', id],
    queryFn: () => customerPaymentAPI.listByCustomer(id!).then(r => r.data || []),
    enabled: !!id && activeTab === 'payments',
    placeholderData: [],
  })

  if (customerError) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <p className="text-gray-600">Customer not found</p>
          <Button onClick={() => navigate('/customers')} className="mt-4">Back to Customers</Button>
        </div>
      </div>
    )
  }

  if (customerLoading) {
    return (
      <div className="space-y-4">
        <div className="h-16 bg-gray-200 rounded-lg animate-pulse" />
        <div className="h-32 bg-gray-200 rounded-lg animate-pulse" />
      </div>
    )
  }

  if (!customer) return null

  const tabs = [
    { id: 'vehicles' as const, label: 'Vehicles', count: customer.vehicle_count },
    { id: 'licences' as const, label: 'Licences', count: customer.licence_count },
    { id: 'insurances' as const, label: 'Insurances', count: customer.insurance_count },
    { id: 'pucs' as const, label: 'PUCs', count: customer.puc_count },
    { id: 'fitness' as const, label: 'Fitness', count: customer.fitness_count },
    { id: 'road_tax' as const, label: 'Road Tax', count: customer.road_tax_count },
    { id: 'env_tax' as const, label: 'Env Tax', count: customer.env_tax_count },
    { id: 'payments' as const, label: 'Payments', count: payments?.length || 0 },
  ]

  const formatVehicleType = (vehicleType?: string | null) => {
    if (!vehicleType) return 'Not set'

    const labels: Record<string, string> = {
      two_wheeler: '2 Wheeler',
      three_wheeler: '3 Wheeler',
      four_wheeler: '4 Wheeler',
      commercial: 'Commercial',
      other: 'Other',
    }

    return labels[vehicleType] || vehicleType.replace(/_/g, ' ')
  }

  const formatFuelType = (fuelType?: string | null) => {
    if (!fuelType) return 'Not set'

    const labels: Record<string, string> = {
      petrol: 'Petrol',
      diesel: 'Diesel',
      electric: 'Electric',
      cng: 'CNG',
      hybrid: 'Hybrid',
      lpg: 'LPG',
    }

    return labels[fuelType] || fuelType.toUpperCase()
  }

  const getTabContent = () => {
    const isLoading = {
      vehicles: vehiclesLoading,
      licences: licencesLoading,
      insurances: insurancesLoading,
      pucs: pucsLoading,
      fitness: fitnessLoading,
      road_tax: roadTaxLoading,
      env_tax: envTaxLoading,
      payments: paymentsLoading,
    }[activeTab]

    const dataMap = {
      vehicles: vehicles || [],
      licences: licences || [],
      insurances: insurances || [],
      pucs: pucs || [],
      fitness: fitness || [],
      road_tax: roadTaxes || [],
      env_tax: envTaxes || [],
      payments: payments || [],
    }
    
    const data = Array.isArray(dataMap[activeTab as keyof typeof dataMap]) 
      ? dataMap[activeTab as keyof typeof dataMap] 
      : []

    if (isLoading) {
      return <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />)}</div>
    }

    if (!data || data.length === 0) {
      return <div className="flex h-48 items-center justify-center text-gray-500">No {activeTab} records</div>
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200">
            <tr className="text-gray-600">
              {activeTab === 'vehicles' && (
                <>
                  <th className="text-left py-3 px-4">Registration</th>
                  <th className="text-left py-3 px-4">Make/Model</th>
                  <th className="text-left py-3 px-4">Type/Fuel</th>
                  {/* <th className="text-left py-3 px-4">Compliance</th> */}
                  <th className="text-right py-3 px-4">Actions</th>
                </>
              )}
              {activeTab === 'licences' && (
                <>
                  <th className="text-left py-3 px-4">Licence #</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-left py-3 px-4">Classes</th>
                  <th className="text-left py-3 px-4">Expiry</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </>
              )}
              {activeTab === 'insurances' && (
                <>
                  <th className="text-left py-3 px-4">Vehicle</th>
                  <th className="text-left py-3 px-4">Policy #</th>
                  <th className="text-left py-3 px-4">Company</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-left py-3 px-4">Coverage</th>
                  <th className="text-left py-3 px-4">Premium</th>
                  <th className="text-left py-3 px-4">Expiry</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </>
              )}
              {activeTab === 'pucs' && (
                <>
                  <th className="text-left py-3 px-4">Cert #</th>
                  <th className="text-left py-3 px-4">Test Date</th>
                  <th className="text-left py-3 px-4">Expiry</th>
                  <th className="text-left py-3 px-4">Result</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </>
              )}
              {activeTab === 'fitness' && (
                <>
                  <th className="text-left py-3 px-4">Cert #</th>
                  <th className="text-left py-3 px-4">Class</th>
                  <th className="text-left py-3 px-4">Inspection Date</th>
                  <th className="text-left py-3 px-4">Expiry</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </>
              )}
              {activeTab === 'road_tax' && (
                <>
                  <th className="text-left py-3 px-4">Receipt #</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-left py-3 px-4">Period</th>
                  <th className="text-left py-3 px-4">Valid Until</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </>
              )}
              {activeTab === 'env_tax' && (
                <>
                  <th className="text-left py-3 px-4">Receipt #</th>
                  <th className="text-left py-3 px-4">Category</th>
                  <th className="text-left py-3 px-4">Valid Until</th>
                  <th className="text-left py-3 px-4">Linked Fitness</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </>
              )}
              {activeTab === 'payments' && (
                <>
                  <th className="text-left py-3 px-4">Total Bill</th>
                  <th className="text-left py-3 px-4">Paid Amount</th>
                  <th className="text-left py-3 px-4">Pending</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Date</th>
                  {/* <th className="text-right py-3 px-4">Actions</th> */}
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((item: any, idx: number) => (
              <tr key={idx} className="hover:bg-gray-50">
                {activeTab === 'vehicles' && (
                  <>
                    <td className="py-3 px-4 font-medium">{item.registration_number}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">
                        {item.make || item.model
                          ? [item.make, item.model].filter(Boolean).join(' / ')
                          : 'Not provided'}
                      </div>
                      {item.variant && (
                        <div className="text-xs text-gray-500 mt-0.5">{item.variant}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-xs bg-gray-100 px-2 py-1 rounded inline-block">
                        {formatVehicleType(item.vehicle_type)}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        Fuel: {formatFuelType(item.fuel_type)}
                      </div>
                    </td>
                    {/* <td className="py-3 px-4">{item.compliance_score}/5</td> */}
                    <td className="py-3 px-4 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/customers/${id}/vehicles/${item.id}`)}
                      >
                        View Details
                      </Button>
                    </td>
                  </>
                )}
                {activeTab === 'licences' && (
                  <>
                    <td className="py-3 px-4 font-medium">{item.licence_number}</td>
                    <td className="py-3 px-4">{item.licence_type}</td>
                    <td className="py-3 px-4 text-xs">{item.vehicle_classes?.join(', ')}</td>
                    <td className="py-3 px-4"><ExpiryBadge days={daysRemaining(item.expiry_date)} /></td>
                    <td className="py-3 px-4 text-right"><Button size="sm" variant="secondary" onClick={() => {
                      setSelectedLicence(item);
                      setShowLicenceModal(true);
                    }}>Edit</Button></td>
                  </>
                )}
                {activeTab === 'insurances' && (
                  <>
                    <td className="py-3 px-4 font-medium">{item.vehicle_reg}</td>
                    <td className="py-3 px-4 font-medium">{item.policy_number}</td>
                    <td className="py-3 px-4">{item.insurance_company || '-'}</td>
                    <td className="py-3 px-4"><span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">{item.policy_type || '-'}</span></td>
                    <td className="py-3 px-4">{item.coverage_amount ? parseInt(item.coverage_amount) : '-'}</td>
                    <td className="py-3 px-4">{item.premium_amount ? parseInt(item.premium_amount) : '-'}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <ExpiryBadge days={daysRemaining(item.expiry_date)} />
                        <span className="text-xs text-gray-600">{formatDate(item.expiry_date)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right"><Button size="sm" variant="secondary" onClick={() => {
                      setSelectedInsurance(item);
                      setShowAddInsuranceModal(true);
                    }}>Edit</Button></td>
                  </>
                )}
                {activeTab === 'pucs' && (
                  <>
                    <td className="py-3 px-4 font-medium">{item.certificate_number}</td>
                    <td className="py-3 px-4">{formatDate(item.test_date)}</td>
                    <td className="py-3 px-4"><ExpiryBadge days={daysRemaining(item.expiry_date)} /></td>
                    <td className="py-3 px-4"><span className={`text-xs px-2 py-1 rounded ${item.result === 'pass' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{item.result}</span></td>
                    <td className="py-3 px-4 text-right"><Button size="sm" variant="secondary" onClick={() => {
                      setSelectedPUC(item);
                      setShowAddPUCModal(true);
                    }}>Edit</Button></td>
                  </>
                )}
                {activeTab === 'fitness' && (
                  <>
                    <td className="py-3 px-4 font-medium">{item.certificate_number}</td>
                    <td className="py-3 px-4">{item.fitness_class}</td>
                    <td className="py-3 px-4">{formatDate(item.inspection_date)}</td>
                    <td className="py-3 px-4"><ExpiryBadge days={daysRemaining(item.expiry_date)} /></td>
                    <td className="py-3 px-4 text-right"><Button size="sm" variant="secondary" onClick={() => {
                      setSelectedFitness(item);
                      setShowAddFitnessModal(true);
                    }}>Edit</Button></td>
                  </>
                )}
                {activeTab === 'road_tax' && (
                  <>
                    <td className="py-3 px-4 font-medium">{item.receipt_number}</td>
                    <td className="py-3 px-4">{item.tax_type}</td>
                    <td className="py-3 px-4">{item.tax_period}</td>
                    <td className="py-3 px-4">{item.is_lifetime ? 'Lifetime' : <ExpiryBadge days={daysRemaining(item.valid_until)} />}</td>
                    <td className="py-3 px-4 text-right"><Button size="sm" variant="secondary" onClick={() => {
                      setSelectedRoadTax(item);
                      setShowAddRoadTaxModal(true);
                    }}>Edit</Button></td>
                  </>
                )}
                {activeTab === 'env_tax' && (
                  <>
                    <td className="py-3 px-4 font-medium">{item.receipt_number}</td>
                    <td className="py-3 px-4">{item.tax_category}</td>
                    <td className="py-3 px-4"><ExpiryBadge days={daysRemaining(item.valid_until)} /></td>
                    <td className="py-3 px-4">{item.linked_fitness_cert || '-'}</td>
                    <td className="py-3 px-4 text-right"><Button size="sm" variant="secondary" onClick={() => {
                      setSelectedEnvTax(item);
                      setShowAddEnvTaxModal(true);
                    }}>Edit</Button></td>
                  </>
                )}
                {activeTab === 'payments' && (
                  <>
                    <td className="py-3 px-4 font-medium">₹{item.total_bill_amount.toFixed(2)}</td>
                    <td className="py-3 px-4 text-green-600 font-medium">₹{item.paid_amount.toFixed(2)}</td>
                    <td className="py-3 px-4 text-red-600 font-medium">₹{item.pending_amount.toFixed(2)}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                        item.payment_status === 'completed' ? 'bg-green-100 text-green-800' :
                        item.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {item.payment_status === 'completed' ? '✅ Completed' :
                         item.payment_status === 'partial' ? '⏳ Partial' :
                         '⏳ Pending'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{formatDateTime(item.created_at)}</td>
                    {/* <td className="py-3 px-4 text-right"><Button size="sm" variant="secondary">View</Button></td> */}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div>
      <button
        onClick={() => navigate("/customers")}
        className="flex items-center gap-2 text-saffron mb-6 hover:text-orange-600"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Customers
      </button>

      {/* Customer Header Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
          <div className="w-16 h-16 rounded-full bg-saffron text-white flex items-center justify-center text-xl font-bold">
            {getInitials(customer.full_name)}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">
              {customer.full_name}
            </h1>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {customer.phone}
              </div>
            </div>

            {/* Payment Summary */}
            {paymentSummary && (
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-200">
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-gray-500 font-medium">
                    Total Billed:
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    ₹{paymentSummary.total_billed.toFixed(2)}
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-gray-500 font-medium">
                    Total Paid:
                  </span>
                  <span className="text-sm font-bold text-green-600">
                    ₹{paymentSummary.total_paid.toFixed(2)}
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-gray-500 font-medium">
                    Pending:
                  </span>
                  <span
                    className={`text-sm font-bold ${paymentSummary.pending_amount > 0 ? "text-red-600" : "text-green-600"}`}
                  >
                    ₹{paymentSummary.pending_amount.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <Button
            className="bg-saffron hover:bg-orange-600"
            onClick={() => setShowEditModal(true)}
          >
            Edit
          </Button>
        </div>
      </div>

      {/* Summary Chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-6">
        <button
          onClick={() => setActiveTab("vehicles")}
          className="p-3 rounded-lg bg-blue-50 hover:bg-blue-100 text-center cursor-pointer"
        >
          <div className="text-2xl font-bold text-blue-600">
            {customer.vehicle_count}
          </div>
          <div className="text-xs text-blue-700">Vehicles</div>
        </button>
        <button
          onClick={() => setActiveTab("licences")}
          className="p-3 rounded-lg bg-purple-50 hover:bg-purple-100 text-center cursor-pointer"
        >
          <div className="text-2xl font-bold text-purple-600">
            {customer.licence_count}
          </div>
          <div className="text-xs text-purple-700">Licences</div>
        </button>
        <button
          onClick={() => setActiveTab("insurances")}
          className="p-3 rounded-lg bg-pink-50 hover:bg-pink-100 text-center cursor-pointer"
        >
          <div className="text-2xl font-bold text-pink-600">
            {customer.insurance_count}
          </div>
          <div className="text-xs text-pink-700">Insurances</div>
        </button>
        <button
          onClick={() => setActiveTab("pucs")}
          className="p-3 rounded-lg bg-teal-50 hover:bg-teal-100 text-center cursor-pointer"
        >
          <div className="text-2xl font-bold text-teal-600">
            {customer.puc_count}
          </div>
          <div className="text-xs text-teal-700">PUCs</div>
        </button>
        <button
          onClick={() => setActiveTab("fitness")}
          className="p-3 rounded-lg bg-green-50 hover:bg-green-100 text-center cursor-pointer"
        >
          <div className="text-2xl font-bold text-green-600">
            {customer.fitness_count}
          </div>
          <div className="text-xs text-green-700">Fitness</div>
        </button>
        <button
          onClick={() => setActiveTab("road_tax")}
          className="p-3 rounded-lg bg-orange-50 hover:bg-orange-100 text-center cursor-pointer"
        >
          <div className="text-2xl font-bold text-orange-600">
            {customer.road_tax_count}
          </div>
          <div className="text-xs text-orange-700">Road Tax</div>
        </button>
        <button
          onClick={() => setActiveTab("env_tax")}
          className="p-3 rounded-lg bg-lime-50 hover:bg-lime-100 text-center cursor-pointer"
        >
          <div className="text-2xl font-bold text-lime-600">
            {customer.env_tax_count}
          </div>
          <div className="text-xs text-lime-700">Env Tax</div>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-saffron text-saffron"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              {tab.label}{" "}
              {tab.id !== "payments" && (
                <span className="text-xs text-gray-500">({tab.count})</span>
              )}
            </button>
          ))}
        </div>

        <div className="p-4">
          <div className="flex justify-end mb-4">
            {activeTab !== "payments" && (
              <Button
                size="sm"
                className="bg-saffron text-white"
                onClick={() => {
                  if (activeTab === "vehicles") {
                    setShowAddVehicleModal(true);
                  } else if (activeTab === "licences") {
                    setSelectedLicence(undefined);
                    setShowLicenceModal(true);
                  } else if (activeTab === "insurances") {
                    setSelectedInsurance(undefined);
                    setShowAddInsuranceModal(true);
                  } else if (activeTab === "pucs") {
                    setSelectedPUC(undefined);
                    setShowAddPUCModal(true);
                  } else if (activeTab === "fitness") {
                    setSelectedFitness(undefined);
                    setShowAddFitnessModal(true);
                  } else if (activeTab === "road_tax") {
                    setSelectedRoadTax(undefined);
                    setShowAddRoadTaxModal(true);
                  } else if (activeTab === "env_tax") {
                    setSelectedEnvTax(undefined);
                    setShowAddEnvTaxModal(true);
                  }
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add {activeTab}
              </Button>
            )}
          </div>
          {activeTab === "payments" ? (
            <div className="space-y-6">
              {/* Payment Form */}
              <PaymentForm
                customerId={id!}
                customerName={customer.full_name}
                summary={paymentSummary}
                isLoading={paymentSummaryLoading}
                onPaymentAdded={() => {
                  queryClient.invalidateQueries({ queryKey: ["payments", id] });
                  queryClient.invalidateQueries({
                    queryKey: ["payment_summary", id],
                  });
                }}
                hideAddButton={true}
              />

              {/* Payment History */}
              {payments && payments.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    📋 Payment History
                  </h3>
                  {getTabContent()}
                </div>
              )}
            </div>
          ) : (
            getTabContent()
          )}
        </div>
      </div>

      <AddCustomerModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        customer={customer}
        onSuccess={() => {
          setShowEditModal(false);
          // Invalidate customer query to refresh data
          queryClient.invalidateQueries({ queryKey: ["customer", id] });
        }}
      />

      <AddVehicleModal
        open={showAddVehicleModal}
        onClose={() => setShowAddVehicleModal(false)}
        customerId={id!}
        onSuccess={() => {
          setShowAddVehicleModal(false);
          // Invalidate vehicle queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["vehicles", id] });
          queryClient.invalidateQueries({ queryKey: ["customer", id] });
        }}
      />

      <AddLicenceModal
        open={showLicenceModal}
        onClose={() => {
          setShowLicenceModal(false);
          setSelectedLicence(undefined);
        }}
        customerId={id!}
        licence={selectedLicence}
        onSuccess={() => {
          setShowLicenceModal(false);
          setSelectedLicence(undefined);
          // Invalidate licence queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["licences", id] });
          queryClient.invalidateQueries({ queryKey: ["customer", id] });
        }}
      />

      <AddInsuranceModal
        open={showAddInsuranceModal}
        onClose={() => {
          setShowAddInsuranceModal(false);
          setSelectedInsurance(undefined);
        }}
        customerId={id!}
        insurance={selectedInsurance}
        onSuccess={() => {
          setShowAddInsuranceModal(false);
          setSelectedInsurance(undefined);
          // Invalidate insurance queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["insurances", id] });
          queryClient.invalidateQueries({ queryKey: ["customer", id] });
        }}
      />

      <AddPUCModal
        open={showAddPUCModal}
        onClose={() => {
          setShowAddPUCModal(false);
          setSelectedPUC(undefined);
        }}
        customerId={id!}
        puc={selectedPUC}
        onSuccess={() => {
          setShowAddPUCModal(false);
          setSelectedPUC(undefined);
          // Invalidate PUC queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["pucs", id] });
          queryClient.invalidateQueries({ queryKey: ["customer", id] });
        }}
      />

      <AddFitnessModal
        open={showAddFitnessModal}
        onClose={() => {
          setShowAddFitnessModal(false);
          setSelectedFitness(undefined);
        }}
        customerId={id!}
        fitness={selectedFitness}
        onSuccess={() => {
          setShowAddFitnessModal(false);
          setSelectedFitness(undefined);
          // Invalidate fitness queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["fitness", id] });
          queryClient.invalidateQueries({ queryKey: ["customer", id] });
        }}
      />

      <AddRoadTaxModal
        open={showAddRoadTaxModal}
        onClose={() => {
          setShowAddRoadTaxModal(false);
          setSelectedRoadTax(undefined);
        }}
        customerId={id!}
        roadTax={selectedRoadTax}
        onSuccess={() => {
          setShowAddRoadTaxModal(false);
          setSelectedRoadTax(undefined);
          // Invalidate road tax queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["road_taxes", id] });
          queryClient.invalidateQueries({ queryKey: ["customer", id] });
        }}
      />

      <AddEnvTaxModal
        open={showAddEnvTaxModal}
        onClose={() => {
          setShowAddEnvTaxModal(false);
          setSelectedEnvTax(undefined);
        }}
        customerId={id!}
        envTax={selectedEnvTax}
        onSuccess={() => {
          setShowAddEnvTaxModal(false);
          setSelectedEnvTax(undefined);
          // Invalidate env tax queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["env_taxes", id] });
          queryClient.invalidateQueries({ queryKey: ["customer", id] });
        }}
      />
    </div>
  );
}
